<template>
  <div class="person-line" :class="{'person-line--dense': dense}">
    <ontos-user-avatar :user="user" :label="label" :photo="photo" :size="dense ? 24 : 36"/>
    <div class="person-text">
      <span class="person-name">{{ label }}</span>
      <span v-if="name && email" class="person-email">{{ email }}</span>
    </div>
    <!-- O que se faz com esta pessoa fica JUNTO dela, e não na borda da tela: pendurado no
         `append` da linha do Vuetify, o nível acabava a meio metro do nome num monitor largo, e
         a vista tinha de atravessar o vazio para ligar um ao outro. -->
    <div v-if="$slots.default" class="person-actions"><slot/></div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import UserAvatar from '@/components/shared/UserAvatar.vue'

/**
 * Uma pessoa numa lista: a cara e o nome — e o endereço, nas telas de administração.
 *
 * **O e-mail só aparece onde é passado**: nas pessoas de um ambiente, nos convites e na sugestão
 * do convite, que são de quem administra. No modo de uso ele nem chega ao navegador (decisão do
 * usuário: quem edita não precisa saber o endereço de ninguém), e o que distingue dois homônimos
 * é o id da conta — que dá também a cor do avatar.
 *
 * **Sem nome, o e-mail ocupa o lugar dele**, onde há e-mail: quem foi convidado e ainda não entrou
 * no lunedi não tem nome. Sem os dois, uma frase fixa — o nome é obrigatório na conta, e isso só
 * acontece com quem ainda não o preencheu.
 */
@Component({name: 'ontos-person-line', components: {'ontos-user-avatar': UserAvatar}})
class PersonLine extends Vue {
  /** O id da conta: o que distingue a pessoa, e o que dá a cor do avatar. */
  @Prop({type: Number, required: true}) readonly user!: number
  @Prop({type: String, default: ''}) readonly email!: string
  @Prop({type: String, default: ''}) readonly name!: string
  @Prop({type: String, default: ''}) readonly photo!: string
  /**
   * Numa linha só, com a cara menor: para dentro de um campo, como a pessoa escolhida no convite
   * de ambiente. As duas linhas com a cara de 36px estouravam a altura do campo compacto (40px).
   */
  @Prop({type: Boolean, default: false}) readonly dense!: boolean

  /** O que se lê da pessoa — e de onde saem as iniciais do avatar. */
  get label(): string {
    return this.name || this.email || this.$tt('person-unnamed') as string
  }
}

export default toNative(PersonLine)
</script>

<style scoped>
.person-line {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
}

/* Largura limitada, e não livre: assim as ações de todas as linhas começam quase no mesmo ponto
   — nome curto e nome longo não deslocam a coluna —, sem que elas precisem ir para a borda da
   tela para se alinharem. */
.person-text {
  display: flex;
  flex: 0 1 280px;
  flex-direction: column;
  line-height: 1.3;
  min-width: 0;
}

.person-line--dense {
  gap: 8px;
}

.person-line--dense .person-text {
  align-items: baseline;
  flex: 0 1 auto;
  flex-direction: row;
  gap: 8px;
}

/* Sem espaço, quem cede primeiro é o e-mail: o nome é o que se reconhece de relance */
.person-line--dense .person-email {
  flex-shrink: 10;
}

.person-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  margin-left: 16px;
}

/* Não é o `shell-label`/`shell-value` do casco, e de propósito: aquele rótulo é caixa alta de
   10px, medida de legenda — e endereço de e-mail em versais com 10px não se lê. A hierarquia é a
   mesma; os tamanhos é que são de conteúdo, não de barra. */
.person-name {
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.person-email {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
